import React from "react";
import { Button, Form } from "react-bootstrap";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import type { KpiOption } from "../types/kpiEntryMode";
import KpiOptionSourceEditor from "./KpiOptionSourceEditor";

type Props = {
  options: KpiOption[];
  minSelections: number | "";
  maxSelections: number | "";
  onChangeOptions: (next: KpiOption[]) => void;
  onChangeMin: (value: number | "") => void;
  onChangeMax: (value: number | "") => void;
  /** When true, max selections is locked to 1 (SELECT). */
  singleSelect?: boolean;
  /** Predefined list code; null/empty = typed options. */
  predefinedOptionListCode?: string | null;
  /** Switching to a list clears the typed options; null goes back to typed options. */
  onChangePredefinedOptionListCode?: (code: string | null) => void;
};

function renumber(options: KpiOption[]): KpiOption[] {
  return options.map((opt, idx) => ({ ...opt, list_order: idx }));
}

function splitOther(options: KpiOption[]): {
  normal: KpiOption[];
  other: KpiOption | null;
} {
  const other = options.find((o) => !!o.allows_input) ?? null;
  const normal = options.filter((o) => !o.allows_input);
  return { normal, other };
}

const KpiOptionsEditor: React.FC<Props> = ({
  options,
  minSelections,
  maxSelections,
  onChangeOptions,
  onChangeMin,
  onChangeMax,
  singleSelect = false,
  predefinedOptionListCode = null,
  onChangePredefinedOptionListCode,
}) => {
  const { normal, other } = splitOther(options);
  const usesList = !!predefinedOptionListCode;

  const changeSource = (code: string | null) => {
    if (!onChangePredefinedOptionListCode) return;
    if (code && !usesList) {
      onChangeOptions([]);
    }
    onChangePredefinedOptionListCode(code);
  };

  const publish = (nextNormal: KpiOption[], nextOther: KpiOption | null) => {
    onChangeOptions(
      renumber(nextOther ? [...nextNormal, nextOther] : nextNormal)
    );
  };

  const addOption = () => {
    publish(
      [
        ...normal,
        {
          label: "",
          list_order: normal.length,
          allows_input: false,
          is_active: true,
        },
      ],
      other
    );
  };

  const addOther = () => {
    if (other) return;
    publish(normal, {
      label: "Other",
      list_order: normal.length,
      allows_input: true,
      is_active: true,
    });
  };

  const updateNormal = (index: number, patch: Partial<KpiOption>) => {
    const nextNormal = normal.map((opt, i) =>
      i === index ? { ...opt, ...patch, allows_input: false } : opt
    );
    publish(nextNormal, other);
  };

  const updateOther = (patch: Partial<KpiOption>) => {
    if (!other) return;
    publish(normal, { ...other, ...patch, allows_input: true });
  };

  const removeNormal = (index: number) => {
    publish(
      normal.filter((_, i) => i !== index),
      other
    );
  };

  const removeOther = () => {
    publish(normal, null);
  };

  return (
    <div data-testid="kpi-options-editor">
      <div className="d-flex flex-wrap gap-3 mb-3">
        <Form.Group controlId="kpiMinSelections" style={{ maxWidth: 160 }}>
          <Form.Label>Min selections</Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={minSelections === "" ? "" : minSelections}
            onChange={(e) =>
              onChangeMin(e.target.value === "" ? "" : Number(e.target.value))
            }
          />
        </Form.Group>
        <Form.Group controlId="kpiMaxSelections" style={{ maxWidth: 160 }}>
          <Form.Label>Max selections</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={singleSelect ? 1 : maxSelections === "" ? "" : maxSelections}
            disabled={singleSelect}
            onChange={(e) =>
              onChangeMax(e.target.value === "" ? "" : Number(e.target.value))
            }
          />
          {singleSelect ? (
            <Form.Text muted>SELECT always allows one choice.</Form.Text>
          ) : null}
        </Form.Group>
      </div>

      {onChangePredefinedOptionListCode ? (
        <KpiOptionSourceEditor
          idPrefix="kpi-options"
          code={predefinedOptionListCode}
          onChangeCode={changeSource}
          typedOptionCount={options.length}
          target="kpi"
        />
      ) : null}

      {usesList ? null : (
      <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold">Options</div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="btn-action"
            onClick={addOption}
            data-testid="kpi-option-add"
          >
            <FiPlus className="me-1" />
            Add option
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="btn-action"
            onClick={addOther}
            disabled={!!other}
            data-testid="kpi-option-add-other"
          >
            <FiPlus className="me-1" />
            Add &quot;Other&quot;
          </Button>
        </div>
      </div>

      {options.length === 0 ? (
        <div className="text-muted small">No options yet.</div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {normal.map((opt, index) => (
            <div
              key={opt.kpi_option_id ?? `normal-${index}`}
              className="border rounded p-2"
              data-testid={`kpi-option-row-${index}`}
            >
              <div className="d-flex gap-2 align-items-start">
                <Form.Control
                  type="text"
                  placeholder="Option label"
                  value={opt.label}
                  onChange={(e) =>
                    updateNormal(index, { label: e.target.value })
                  }
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ntcog-table-action-btn--danger flex-shrink-0"
                  onClick={() => removeNormal(index)}
                  aria-label={`Remove option ${index + 1}`}
                >
                  <FiTrash2 />
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-3 mt-2">
                <Form.Check
                  type="switch"
                  id={`kpi-option-active-${index}`}
                  label="Active"
                  checked={opt.is_active !== false}
                  onChange={(e) =>
                    updateNormal(index, { is_active: e.target.checked })
                  }
                />
              </div>
            </div>
          ))}

          {other ? (
            <div
              className="border rounded p-2"
              data-testid="kpi-option-row-other"
            >
              <div className="d-flex gap-2 align-items-start">
                <Form.Control
                  type="text"
                  placeholder="Other"
                  value={other.label}
                  onChange={(e) => updateOther({ label: e.target.value })}
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ntcog-table-action-btn--danger flex-shrink-0"
                  onClick={removeOther}
                  aria-label="Remove Other option"
                >
                  <FiTrash2 />
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-3 mt-2 align-items-center">
                <span className="badge text-bg-secondary">Allows free text</span>
                <Form.Check
                  type="switch"
                  id="kpi-option-other-active"
                  label="Active"
                  checked={other.is_active !== false}
                  onChange={(e) =>
                    updateOther({ is_active: e.target.checked })
                  }
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default KpiOptionsEditor;
